import { ElementOf, ensureNumber } from './tsHelpers';

export function paginate<T = unknown>(data: T[], pageSize: number, currPageNum = 1): T[] {
  const startIndex = (currPageNum - 1) * pageSize;
  const endIndex = Math.min(data.length, startIndex + pageSize);

  if (startIndex >= endIndex && currPageNum !== 1) {
    return [];
  }

  return data.slice(startIndex, endIndex);
}

/**
 * Given an array of objects, sort the array using a key of the object.
 * Returns the sorted array
 */
export function sortObjectListByKey<TObject = Record<string, unknown>>(
  list: TObject[],
  key: keyof TObject,
  descending = false,
) {
  const sorted = [...list];
  sorted.sort((a, b) => {
    // properly sort numeric strings ie. '1' < '6' < '12'
    const isNumber = !!parseInt(`${a[key]}`, 10);
    return `${a[key]}`.localeCompare(`${b[key]}`, 'en-US', { numeric: isNumber });
  });
  if (descending) {
    sorted.reverse();
  }

  return sorted;
}

/**
 * Given an array, break it into two arrays through a test
 * returns a tuple of [matchedItems[], unmatchedItems[]]
 */
export const splitArray = <T, U>(arr: (T | U)[], test: (item: T | U) => boolean): [T[], U[]] => {
  const matchedArray: T[] = [];
  const unmatchedarray: U[] = [];
  arr.forEach((item: T | U) => {
    const itemMatchesTest = test(item);
    if (itemMatchesTest) {
      matchedArray.push(item as T);
    } else {
      unmatchedarray.push(item as U);
    }
  });
  return [matchedArray, unmatchedarray];
};

/**
 * Given an array of objects, calculate the sum of values by a key of the object.
 */
export function sumObjectListByKey<TObject = Record<string, unknown>>(
  list: TObject[],
  key: keyof TObject,
) {
  return list.reduce((aggregate: number, item) => aggregate + ensureNumber(item[key]), 0);
}

export const toggleElementInArray = <ElementType>(element: ElementType, array: ElementType[]) =>
  array.includes(element)
    ? array.filter((arrayElement) => arrayElement !== element)
    : [...array, element];

/**
 * Helper function that keys array elements by a function's return value
 * Consider using _.keyBy if the output from this function is slightly different than you want.
 */
export const keyBy = <ArrayType extends any[]>(
  arr: ArrayType,
  getKey: (forElement: ElementOf<ArrayType>) => any,
): Record<string, ElementOf<ArrayType>[]> =>
  arr.reduce((acc, element) => {
    const key = getKey(element);
    acc[key] ??= [];
    acc[key].push(element);
    return acc;
  }, {});

/**
 * Helper function that returns a sorted array of keys from a given object
 */
export const keysOf = <KeyType extends string | number | symbol>(
  obj: Record<KeyType, any>,
): KeyType[] => (Object.keys(obj) as KeyType[]).sort();

/**
  Given a denormalized list of objects, return a unique array of values for a property
 */
export const dedupeListValuesByProperty = <ArrayType extends any[]>(
  options: ArrayType,
  property: string,
): string[] => {
  const fullSetOfValues = options.reduce((setOfValues, listItem) => {
    const val = listItem[property];

    // 0's are allowed, but other falsy values will cause buried failures here
    if (!val && val !== 0) {
      throw new Error(`Cannot dedupe list with item missing required property ${property}`);
    }

    setOfValues.add(val);
    return setOfValues;
  }, new Set());

  return Array.from(fullSetOfValues);
};

export const removeElementAt = <Element>(arr: Element[], index: number) => [
  ...arr.slice(0, index),
  ...arr.slice(index + 1),
];

export const replaceElementAt = <Element>(arr: Element[], index: number, element: Element) => [
  ...arr.slice(0, index),
  element,
  ...arr.slice(index + 1),
];

export const pick = <T, K extends keyof T>(arr: T[], key: K): T[K][] =>
  arr.map((element) => element[key]);
